'use client';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { elowenClient } from '../../lib/elowenClient';
import { QUERY_KEYS } from '../../lib/queries';
import { Avatar as AvatarRoot, AvatarFallback, AvatarImage } from './shadcn/avatar';

/** How many identity chips exist. Eight leaves neighbouring hues 45° apart — far enough to tell two
 *  people apart at 36px — while keeping a collision in one team unlikely. */
const IDENTITY_STEPS = 8;
/** Two letters, chosen by what the label actually is. A full name gives one initial per word, so
 *  "Dana Nováková" reads as DN — plain `slice(0, 2)` said DA, which is why the team rail kept its own
 *  copy of this. A single word (a bare username) has no second word to draw from, so it keeps its first
 *  two letters: "alex" as AL is more distinguishable than a lone A. Everything is taken by CODE POINT;
 *  slicing UTF-16 units halves an astral first letter. */
const initialsOf = (s: string) => {
  const words = s.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const letters = words.length === 1
    ? [...words[0]!].slice(0, 2)
    : words.slice(0, 2).map((w) => [...w][0] ?? '');
  return letters.join('').toUpperCase() || '?';
};
/** A label's identity chip, as a hue rotation off the brand primary. Deterministic — the same person
 *  always gets the same chip — and summed by CODE POINT, like {@link initialsOf}, so a non-ASCII name
 *  buckets like any other.
 *
 *  The eight colours used to be eight hex literals, which froze the identity ramp at the built-in
 *  design's ember: it survived every skin and every repaint, and it carried `text-white` ink that was
 *  ~2:1 on its own amber step. Rotating `bg-primary` instead keeps the ramp on the token layer, so a
 *  skin repaints all eight at once — and because `hue-rotate`'s matrix is luminance-preserving (each
 *  row sums to exactly 1, which also leaves achromatic ink untouched), every chip reads against
 *  `text-primary-foreground` exactly as `bg-primary` itself does. The contrast pairing is therefore
 *  correct by construction rather than per-colour luck.
 *
 *  The trade the old palette was making is real and is accepted here: a design whose primary is nearly
 *  grey gets eight nearly-grey chips. The monogram letters stay the primary identifier in that case,
 *  and a brand with no chroma has nothing to spread eight ways however the colours are chosen. */
export const identityHueOf = (s: string) =>
  ([...s].reduce((sum, c) => sum + c.codePointAt(0)!, 0) % IDENTITY_STEPS) * (360 / IDENTITY_STEPS);

/** Below this diameter two letters at the 12px floor would touch the rim, so the chip shows one. The
 *  22px team stack still holds two. */
const TWO_LETTER_MIN_SIZE = 20;

/** The monogram's type step for each chip diameter: the 12px floor on every small row, the interface step
 *  beside a name, and the figure step on the account header's large chip. */
const FALLBACK_ROLE = (size: number) => (size >= 48 ? 'text-figure' : size >= 32 ? 'text-ui' : 'text-small');

/** How long a minted avatar link is reused before it is asked for again: inside the five minutes the
 *  daemon guarantees every link (`/users/:id/avatar/url` aligns expiry to a five-minute window). */
const AVATAR_URL_FRESH_MS = 4 * 60 * 1000;

/** A user's avatar: the uploaded image when present, else a coloured initials monogram. `src` is a fixed
 *  image that comes first (the agent's mascot in a shared transcript, a plugin's directory photo): while
 *  it loads or shows, the signed link of `user.avatar` is never fetched; only once it fails does the
 *  avatar move on to that upload, then to the monogram. A plugin person photo that answers 404 for
 *  someone without a picture, linked to an account with an upload, walks that whole order.
 *
 *  Composed from the shadcn/ui `Avatar` parts in `./shadcn/avatar.tsx`. What is this file's is only the app's
 *  policy — which label to show, which two letters it becomes, which of eight identity hues backs it,
 *  and where the image URL comes from. The load/error handling under it is Radix's: `AvatarImage` paints
 *  only once the source has decoded and hands back to the fallback if it never does, so a broken avatar
 *  degrades to the monogram instead of a blank square, which the hand-rolled version did not do. */
export function Avatar({ user, size = 36, online = false, src: fixedSrc }: { user: { id: number; username: string; name?: string; avatar?: string }; size?: number; online?: boolean; src?: string }) {
  const label = user.name?.trim() || user.username;
  // The fixed source Radix reported broken. Keyed by the source itself, so a new `src` is tried afresh.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const fixed = fixedSrc !== undefined && fixedSrc !== failedSrc ? fixedSrc : null;
  const uploaded = fixed === null && Boolean(user.avatar);
  // The avatar URL is a short-lived signed link (finding W2). It is ONE cache entry per account, shared by
  // every avatar on screen, and the daemon keeps the link stable for its whole validity window, so a new
  // message's avatar finds the link (and the browser the decoded bytes) already there and paints the image
  // on its first frame instead of the monogram. The entry is refreshed well inside the link's minimum
  // five-minute validity; a failed mint leaves the monogram.
  const { data: signedSrc } = useQuery({
    queryKey: [...QUERY_KEYS.avatarUrl, user.id],
    queryFn: () => elowenClient.avatarUrl(user.id),
    enabled: uploaded,
    staleTime: AVATAR_URL_FRESH_MS,
    retry: false,
  });
  const src = fixed ?? (uploaded ? signedSrc ?? null : null);
  return (
    // The accessible name sits on the ROOT rather than on the image, because the root is the element
    // that is always present: Radix swaps image and fallback underneath it, and a name attached to the
    // image would disappear for exactly the users who have no image.
    // `inline-flex` rather than stock shadcn's block-level `flex`: an avatar here sits beside a name in
    // running text as often as it sits in a grid cell, and a block box there would break the line.
    <span className="relative inline-flex shrink-0" style={{ width: size, height: size }}>
    <AvatarRoot aria-label={label} className="inline-flex" style={{ width: size, height: size }}>
      {src ? (
        <AvatarImage
          src={src}
          alt={label}
          className="rounded-full border border-border"
          onLoadingStatusChange={fixed === null ? undefined : (status) => { if (status === 'error') setFailedSrc(fixed); }}
        />
      ) : null}
      <AvatarFallback
        className={`bg-primary text-primary-foreground ${FALLBACK_ROLE(size)}`}
        // The letters take the role for the chip's diameter (`FALLBACK_ROLE`); the hue is the only per-avatar ink.
        style={{ filter: `hue-rotate(${identityHueOf(label)}deg)` }}
      >
        {size < TWO_LETTER_MIN_SIZE ? [...initialsOf(label)][0] : initialsOf(label)}
      </AvatarFallback>
    </AvatarRoot>
    {online ? <span aria-hidden="true" className="absolute bottom-0 right-0 size-3 rounded-full border-2 border-background bg-success" /> : null}
    </span>
  );
}
